<template>
  <dx-modal :value="show"
            title="Gerenciar atendimentos"
            size="lg"
            @input="$emit('close')"
            id="modal-expense-register"
            class="gd-scope">
    <div class="er-context">
      <div class="er-context-item">
        <small>Paciente</small>
        <div>{{ form.patient.name || '-' }}</div>
      </div>
      <div class="er-context-item">
        <small>Profissional</small>
        <div>{{ form.professional.name || '-' }}</div>
      </div>
      <div class="er-context-item">
        <small>Atendimento</small>
        <div>{{ form.startDate | date('datetime') }}</div>
      </div>
    </div>

    <div class="loading loading-lg" v-if="loading"></div>

    <div class="er-group" v-for="group in groups" :key="group.id" v-else
         :class="{particular: group.particular}">
      <div class="er-group-head">
        <span class="er-group-icon">
          <fa-icon :icon="['fal', group.particular ? 'user' : 'shield-check']"/>
        </span>
        <div class="er-group-title">
          {{ group.name }}
          <small>
            <template v-if="group.plan">{{ group.plan }} · </template>
            {{ group.count }} {{ group.count === 1 ? 'item' : 'itens' }}
            <template v-if="group.pending > 0">
              · <span class="er-group-pending">
                {{ group.pending }} {{ group.pending === 1 ? 'pendente' : 'pendentes' }}
              </span>
            </template>
          </small>
        </div>
        <span class="er-group-done" v-if="group.done">
          <fa-icon :icon="['fal', 'check-circle']"/>
          Registrado
        </span>
        <div class="er-group-total">
          <small>{{ group.particular ? 'A receber' : 'Valor do convênio' }}</small>
          <div>{{ group.total | currency }}</div>
        </div>
      </div>

      <div class="er-group-body">
      <div class="er-section" v-for="section in group.sections" :key="section.id"
           :class="`er-section-${section.state}`">
        <div class="er-section-head">
          <span class="er-section-title">
            {{ section.title }}
            <small v-if="section.subtitle">{{ section.subtitle }}</small>
          </span>
          <guide-status-badge v-if="section.guideStatus" :status="section.guideStatus"
                              :batch-number="section.batchNumber"/>
          <span class="er-section-state" v-else-if="section.state === 'done'">
            <fa-icon :icon="['fal', 'check-circle']"/>{{ section.stateLabel }}
          </span>
          <span class="er-section-state pending" v-else>
            <fa-icon :icon="['fal', 'clock']"/>Pendente
          </span>
          <span class="er-section-unpriced" v-if="section.unpriced > 0">
            {{ section.unpriced }}
            {{ section.unpriced === 1 ? 'item sem preço' : 'itens sem preço' }}
          </span>
          <span class="er-section-actions">
            <template v-if="section.state === 'done' && group.particular">
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Nota fiscal"
                      :class="{loading: fiscalDocument.loading}"
                      :disabled="fiscalDocument.loading || printing"
                      v-if="isFiscalDocumentEnabled && sectionTotal(section) > 0"
                      @click="openEntryInvoice(section)">
                <fa-icon :icon="['fal', 'file-invoice-dollar']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Recibo"
                      :disabled="printing" @click="printReceipt(section.refId)">
                <fa-icon :icon="['fal', 'print']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Estornar" v-if="canReverse"
                      @click="openReverse(section.refId)">
                <fa-icon :icon="['fal', 'undo']"/>
              </button>
            </template>
            <template v-else-if="section.guideStatus">
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Guia de solicitação" :disabled="printing"
                      v-if="section.guideType === 'sadt'"
                      @click="printGuideRequest(section.refId)">
                <fa-icon :icon="['fal', 'file-alt']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Imprimir"
                      :disabled="printing" @click="printGuide(section.refId)">
                <fa-icon :icon="['fal', 'print']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      :data-tooltip="`Ver lote nº ${section.batchNumber}`"
                      v-if="section.batchId && canListBatches" @click="goToBatch(section)">
                <fa-icon :icon="['fal', 'layer-group']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      :data-tooltip="guideLockTooltip(section, 'Editar')"
                      :disabled="section.guideStatus !== 'open' || isFinished || editing"
                      @click="openGuideModal({ accountId: section.refId })">
                <fa-icon :icon="['fal', 'pencil']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left er-delete"
                      :data-tooltip="guideLockTooltip(section, 'Excluir lançamento')"
                      :disabled="section.guideStatus !== 'open' || isFinished || deleting"
                      @click="removeGuide(section.refId)">
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </template>
            <template v-else-if="section.state === 'done'">
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Guia de solicitação"
                      :disabled="printing" v-if="section.tiss"
                      @click="printRequest(section.refId)">
                <fa-icon :icon="['fal', 'file-alt']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Imprimir"
                      :disabled="printing" @click="printAccount(section.refId)">
                <fa-icon :icon="['fal', 'print']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left"
                      data-tooltip="Editar"
                      :disabled="editing" v-if="!isFinished"
                      @click="editAccount(section.refId)">
                <fa-icon :icon="['fal', 'pencil']"/>
              </button>
              <button class="btn btn-sm btn-gray btn-action btn-icon tooltip tooltip-left er-delete"
                      data-tooltip="Excluir lançamento"
                      :disabled="deleting" v-if="!isFinished"
                      @click="removeAccount(section.refId)">
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </template>
            <template v-else-if="group.particular">
              <button class="btn btn-sm btn-secondary btn-icon btn-icon-left"
                      :disabled="working || section.unpriced > 0" @click="openReceive(section)">
                <fa-icon :icon="['fal', 'hand-holding-usd']"/>Receber no caixa
              </button>
            </template>
            <template v-else>
              <button class="btn btn-sm btn-secondary btn-icon btn-icon-left"
                      :class="{loading: working === section.id}"
                      :disabled="!!working" @click="openGuide(group, section)">
                <fa-icon :icon="['fal', 'file-medical-alt']"/>
                {{ section.action }}
              </button>
            </template>
          </span>
        </div>
        <div class="er-items">
          <div class="er-item" v-for="item in section.items" :key="item.key">
            <div class="er-item-main">
              <span class="er-item-service"
                    :title="`${item.name} · ${item.typeName} (${item.code})`">
                <span class="er-item-name">{{ item.name }}</span>
                <small>{{ item.typeName }} ({{ item.code | tuss }})</small>
              </span>
              <div class="er-copay-note" v-if="item.copayOf">
                <span class="gd-pill">
                  Coparticipação · {{ payerOf(item.copayOf).insurance.name }}
                </span>
              </div>
            </div>
            <span class="er-item-qty">x{{ item.quantity }}</span>
            <span class="er-item-value" :class="itemValueClass(item)"
                  v-if="insuranceBillingEnabled">
              <template v-if="item.return">Retorno</template>
              <template v-else-if="group.particular">
                <template v-if="particularTotal(item) > 0">
                  {{ particularTotal(item) | currency }}
                </template>
                <template v-else>
                  —
                  <small v-if="isUnpricedItem(item)">sem preço</small>
                </template>
              </template>
              <template v-else>
                {{ insuranceTotal(item) | currency }}
                <small v-if="isUnpricedItem(item)">sem preço</small>
              </template>
            </span>
            <span class="er-item-value" :class="itemValueClass(item)" v-else>
              <template v-if="item.return">Retorno</template>
              <template v-else>{{ itemTotal(item) | currency }}</template>
            </span>
          </div>
          <div class="er-items-subtotal" v-if="section.items.length > 1">
            <span class="er-subtotal-label">Subtotal</span>
            <span></span>
            <strong v-if="insuranceBillingEnabled && group.particular">
              {{ sectionParticularTotal(section) | currency }}
            </strong>
            <strong v-else-if="insuranceBillingEnabled">
              {{ sectionInsuranceTotal(section) | currency }}
            </strong>
            <strong v-else>{{ sectionTotal(section) | currency }}</strong>
          </div>
        </div>
      </div>
      </div>
    </div>

    <template slot="footer">
      <button class="btn" @click="$emit('close')">Sair</button>
    </template>

    <referral-modal
      v-if="account.show"
      :show="account.show"
      :data="account.data"
      :source="source"
      :type="account.type"
      @close="hideAccountModal"
    />

    <guide-modal
      v-if="guide.show"
      :show="guide.show"
      :account-id="guide.accountId"
      :type="guide.type"
      :appointment="appointmentForGuide()"
      :insurance="guide.insurance"
      :preselected-expense-ids="guide.preselectedExpenseIds"
      source="appointment"
      @saved="guideSaved"
      @deleted="guideDeleted"
      @close="closeGuideModal"
    />

    <reverse-modal
      v-if="reverse.show"
      :show="reverse.show"
      :entry="reverse.entry"
      @close="closeReverse"
    />

    <fiscal-document
      v-if="fiscalDocument.show"
      :show="fiscalDocument.show"
      :entity="fiscalDocument.entity"
      :total-amount="fiscalDocument.totalAmount"
      :origin-type="fiscalDocument.origin.type"
      :origin-id="fiscalDocument.origin.id"
      @loading="fiscalDocument.loading = $event"
      @close="closeFiscalDocument"
    />

    <receive-modal
      v-if="receive.show"
      :show="receive.show"
      :appointment-ids="[form.id]"
      @done="applyReceipt"
      @close="receive.show = false"
    />
  </dx-modal>
</template>

<script>
import moment from 'moment';
import {
  REVERSE_FINANCIAL_TRANSACTION, LIST_BILLINGS, UPDATE_ACCOUNT,
} from '@/data/actions/modules/financial';
import { getLongName as guideLongName } from '@/data/billing-guide-types';
import GuideStatusBadge from '@/app/billing/guide/components/GuideStatusBadge.vue';
import expenseServices from '../../mixins/expenseServices';
import ReceiveModal from '../../../cash-register/modals/Receive.vue';

const GUIDE_ACTIONS = {
  consultation: 'Gerar guia de consulta',
  sadt: 'Gerar guia SP/SADT',
  simple: 'Registrar no convênio',
};

export default {
  mixins: [expenseServices],
  components: {
    ReceiveModal,
    GuideStatusBadge,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      working: null,
      plans: {},
      receive: { show: false },
    };
  },
  computed: {
    canReverse() {
      return this.$can(REVERSE_FINANCIAL_TRANSACTION);
    },
    canListBatches() {
      return this.$can(LIST_BILLINGS);
    },
    canUpdateGuides() {
      return this.$can(UPDATE_ACCOUNT);
    },
    copayItems() {
      if (!this.insuranceBillingEnabled) return [];
      return this.serviceItems
        .filter(item => item.copay.value > 0)
        .map(item => ({
          ...item,
          key: `copay-${item.key}`,
          copayOf: item,
          status: item.copay.paid ? 'paid' : 'scheduled',
        }));
    },
    groups() {
      const hasParticularPayer = this.payers.some(payer => payer.particular);
      const payers = !hasParticularPayer && this.copayItems.length > 0
        ? [...this.payers, {
          id: 'particular', name: 'Particular', insurance: null, particular: true,
        }]
        : this.payers;
      return payers
        .map((payer) => {
          const own = this.serviceItems.filter(item => item.payerId === payer.id);
          const items = payer.particular ? [...own, ...this.copayItems] : own;
          if (items.length === 0) return null;
          const { insurance } = payer;
          const plan = this.plans[payer.id] || null;
          return {
            id: payer.id,
            name: payer.particular ? 'Particular' : insurance.name,
            plan: payer.particular ? null : this.planLabel(insurance),
            insurance,
            particular: payer.particular,
            total: this.groupTotal(items, payer.particular),
            count: items.length,
            pending: items.filter(item => item.status === 'scheduled').length,
            done: items.every(item => item.status !== 'scheduled'),
            sections: payer.particular
              ? this.particularSections(items)
              : this.insuranceSections(items, plan),
          };
        })
        .filter(Boolean);
    },
  },
  mounted() {
    this.loadPlans();
    this.loadGuides();
  },
  methods: {
    planLabel(insurance) {
      const parts = [];
      if (insurance.plan.name && insurance.plan.name !== insurance.name) {
        parts.push(insurance.plan.name);
      }
      if (this.form.insurance.record && this.form.insurance.id === insurance.id) {
        parts.push(`carteirinha ${this.form.insurance.record}`);
      }
      return parts.length > 0 ? parts.join(' · ') : null;
    },
    async loadPlans() {
      this.loading = true;
      const payers = this.payers.filter(payer => !payer.particular
        && this.serviceItems.some(item => item.payerId === payer.id));
      await Promise.all(payers.map(async (payer) => {
        const plan = await this.findInsurancePlan(payer.insurance.id, payer.id);
        if (plan) this.$set(this.plans, payer.id, plan);
      }));
      this.loading = false;
    },
    itemValueClass(item) {
      if (item.return) return 'er-item-return';
      if (this.insuranceBillingEnabled && this.isUnpricedItem(item)) return 'text-warning';
      return this.itemTotal(item) === 0 ? 'text-gray' : '';
    },
    isUnpricedItem(item) {
      if (item.return || item.copayOf) return false;
      return this.payerOf(item).particular
        ? this.particularTotal(item) === 0
        : this.insuranceTotal(item) === 0;
    },
    groupTotal(items, particular) {
      if (!this.insuranceBillingEnabled) {
        return items.reduce((sum, item) => sum + this.itemTotal(item), 0);
      }
      return particular
        ? items.reduce((sum, item) => sum + this.particularTotal(item), 0)
        : items.reduce((sum, item) => sum + this.insuranceTotal(item), 0);
    },
    sectionTotal(section) {
      if (this.insuranceBillingEnabled) {
        return section.items.reduce((sum, item) => sum + this.particularTotal(item), 0);
      }
      return section.items.reduce((sum, item) => sum + this.itemTotal(item), 0);
    },
    sectionInsuranceTotal(section) {
      return section.items.reduce((sum, item) => sum + this.insuranceTotal(item), 0);
    },
    sectionParticularTotal(section) {
      return section.items.reduce((sum, item) => sum + this.particularTotal(item), 0);
    },
    guideLockTooltip(section, action) {
      if (section.guideStatus === 'in_batch') {
        return `No lote nº ${section.batchNumber}. Retire do lote para editar`;
      }
      if (section.guideStatus === 'closed') {
        return `Guia encerrada com o lote nº ${section.batchNumber}`;
      }
      return action;
    },
    guideSectionState(status) {
      if (status === 'closed') return 'closed';
      return 'open';
    },
    goToBatch(section) {
      this.$router.push(`/financial/billing-batches/${section.batchId}`);
    },
    guideHasConsultation(section) {
      const guide = this.guides[section.refId] || null;
      return guide
        ? guide.items.some(item => item.expense.type === 'consultation')
        : section.items.some(item => item.type === 'consultation');
    },
    splitByConsultation(type, pending) {
      if (type !== 'sadt') return [pending];
      const consultations = pending.filter(item => item.type === 'consultation');
      if (consultations.length <= 1) return [pending];
      const [first] = consultations;
      const main = pending.filter(item => item.type !== 'consultation' || item === first);
      return [main, ...consultations.slice(1).map(item => [item])];
    },
    guideSections(items, plan) {
      const tiss = this.tissEnabled(plan);
      const settings = plan && plan.settings ? plan.settings : {};
      const consultationInSadt = !!(tiss && settings.tiss.consultation
        && settings.tiss.consultation.sadt);
      const sections = [];
      const pendingItems = items.filter(item => item.status === 'scheduled');
      const billed = items.filter(item => item.guideId);
      const guideIds = [...new Set(billed.map(item => item.guideId))];
      const guideSections = guideIds.map((guideId) => {
        const guide = this.guides[guideId] || null;
        const guideItems = billed.filter(item => item.guideId === guideId);
        const status = guideItems[0].billingStatus;
        let type = guide ? guide.type : 'simple';
        if (!guide && tiss) {
          type = !consultationInSadt && guideItems.every(item => item.type === 'consultation')
            ? 'consultation'
            : 'sadt';
        }
        const subtitle = [];
        if (status === 'open') subtitle.push('Guia salva · ainda não entrou em nenhum lote');
        if (status === 'in_batch') subtitle.push('Guia já em lote · somente leitura');
        if (status === 'closed') subtitle.push('Lote encerrado');
        return {
          id: `guide-${guideId}`,
          guide: type,
          guideType: type,
          title: type === 'simple' ? 'Conta do convênio' : guideLongName(type),
          subtitle: subtitle.join(' · '),
          state: this.guideSectionState(status),
          guideStatus: status,
          batchNumber: guide && guide.batch ? guide.batch.number : null,
          batchId: guide && guide.batch ? guide.batch.id : null,
          stateLabel: null,
          refId: guideId,
          tiss,
          items: guideItems,
          unpriced: 0,
        };
      });
      let byType = [['simple', () => true]];
      if (tiss && consultationInSadt) {
        byType = [['sadt', () => true]];
      } else if (tiss) {
        byType = [
          ['consultation', item => item.type === 'consultation'],
          ['sadt', item => item.type !== 'consultation'],
        ];
      }
      byType.forEach(([type, predicate]) => {
        const pending = pendingItems.filter(predicate);
        if (pending.length === 0) return;
        const openGuides = type !== 'consultation'
          ? guideSections.filter(section => section.guideStatus === 'open'
            && section.guideType === type)
          : [];
        const taken = new Set(openGuides
          .filter(section => this.guideHasConsultation(section))
          .map(section => section.refId));
        this.splitByConsultation(type, pending).forEach((group, index) => {
          const hasConsultation = type === 'sadt'
            && group.some(item => item.type === 'consultation');
          const openGuide = openGuides
            .find(section => !hasConsultation || !taken.has(section.refId));
          const target = openGuide ? this.guides[openGuide.refId] : null;
          if (target && hasConsultation) taken.add(openGuide.refId);
          sections.push({
            id: index === 0 ? `${type}-pending` : `${type}-pending-${index}`,
            guide: type,
            guideType: type,
            title: type === 'simple' ? 'Conta do convênio' : guideLongName(type),
            subtitle: null,
            action: target ? `Adicionar à guia nº ${target.referral.provider}` : GUIDE_ACTIONS[type],
            targetAccountId: target ? target.id : null,
            state: 'pending',
            guideStatus: null,
            stateLabel: null,
            refId: null,
            tiss,
            items: group,
            unpriced: group.filter(item => this.isUnpricedItem(item)).length,
          });
        });
      });
      const order = { consultation: 0, sadt: 1, simple: 2 };
      const rank = section => (section.guideType in order ? order[section.guideType] : 3);
      return [...guideSections, ...sections].sort((a, b) => rank(a) - rank(b));
    },
    openEntryInvoice(section) {
      this.openFiscalDocument('cash_register_entry', section.refId, this.sectionTotal(section));
    },
    tissEnabled(plan) {
      return !!(plan && plan.settings && plan.settings.tiss && plan.settings.tiss.enabled);
    },
    insuranceSections(items, plan) {
      if (this.insuranceBillingEnabled) return this.guideSections(items, plan);
      const sections = [];
      const tiss = this.tissEnabled(plan);
      const byGuide = (predicate, id, title, action) => {
        const pending = items.filter(item => predicate(item) && item.status === 'scheduled');
        const done = items.filter(item => predicate(item) && item.status === 'guide');
        const accountIds = [...new Set(done.map(item => item.source.accountId))];
        accountIds.forEach((accountId) => {
          sections.push({
            id: `${id}-${accountId}`,
            guide: id,
            title,
            state: 'done',
            stateLabel: 'Lançado',
            refId: accountId,
            tiss,
            items: done.filter(item => item.source.accountId === accountId),
          });
        });
        if (pending.length > 0) {
          sections.push({
            id: `${id}-pending`,
            guide: id,
            title,
            action,
            state: 'pending',
            stateLabel: null,
            refId: null,
            tiss,
            items: pending,
          });
        }
      };
      if (tiss) {
        byGuide(
          item => item.type === 'consultation',
          'consultation',
          'Guia de consulta',
          'Gerar guia de consulta',
        );
        byGuide(item => item.type !== 'consultation', 'sadt', 'Guia SP/SADT', 'Gerar guia SADT');
      } else {
        byGuide(() => true, 'account', 'Conta do convênio', 'Registrar no convênio');
      }
      return sections;
    },
    particularSections(items) {
      const sections = [];
      const done = items.filter(item => item.status === 'paid');
      const pending = items.filter(item => item.status === 'scheduled');
      const entryIds = [...new Set(done.map(item => item.source.cashRegisterEntryId))];
      entryIds.forEach((entryId) => {
        sections.push({
          id: `paid-${entryId}`,
          title: 'Recebido no caixa',
          state: 'done',
          stateLabel: 'Pago',
          refId: entryId,
          items: done.filter(item => item.source.cashRegisterEntryId === entryId),
        });
      });
      if (pending.length > 0) {
        sections.push({
          id: 'pending',
          title: 'A receber',
          state: 'pending',
          stateLabel: null,
          refId: null,
          unpriced: pending.filter(item => this.isUnpricedItem(item)).length,
          items: pending,
        });
      }
      return sections;
    },
    openReceive(section) {
      if (!this.form.id) {
        this.$toast.error('Salve o agendamento antes de receber.');
        return;
      }
      if (section && section.unpriced > 0) {
        this.$toast.error('Atualize o preço dos itens sem valor na aba Serviços antes de receber.');
        return;
      }
      this.receive.show = true;
    },
    async openGuide(group, section) {
      if (!this.form.id) {
        this.$toast.error('Salve o agendamento antes de registrar.');
        return;
      }
      if (this.insuranceBillingEnabled) {
        if (!this.canUpdateGuides) {
          this.$toast.error('Sem permissão para lançar guias.');
          return;
        }
        this.openGuideModal({
          accountId: section.targetAccountId || null,
          type: section.guideType,
          insurance: {
            id: group.insurance.id,
            planId: group.id,
            name: group.name,
          },
          expenseIds: section.items.map(item => item.source.id).filter(Boolean),
        });
        return;
      }
      this.working = section.id;
      try {
        const { insurance } = group;
        const plan = this.plans[group.id]
          || await this.findInsurancePlan(insurance.id, insurance.plan.id);
        if (!plan) {
          this.$toast.error('Plano do convênio não encontrado.');
          return;
        }
        const settings = plan.settings || {};

        let endDate = moment(this.form.startDate)
          .add(15, 'minutes')
          .format('YYYY-MM-DD[T]HH:mm');
        if (this.form.endDate && !moment(this.form.startDate).isSame(this.form.endDate)) {
          endDate = moment(this.form.endDate);
        }

        const sameInsurance = this.form.insurance.id === insurance.id;
        const { professional } = this.form;
        const specialtyCode = professional.specialty || null;

        const account = {
          id: '',
          appointmentId: this.form.id,
          professionalId: professional.id,
          specialtyCode,
          key: this.form.key,
          startDate: this.form.startDate,
          endDate,
          patient: {
            id: this.form.patient.id,
            name: this.form.patient.name,
            birthDate: this.form.patient.birthDate,
            gender: this.form.patient.gender,
            cns: this.form.patient.cns ? this.form.patient.cns : '',
            insurance: {
              id: insurance.id,
              name: insurance.name,
              plan: {
                id: plan.id,
                name: plan.name,
                type: plan.type,
                settings: plan.settings,
              },
              insured: {
                record: sameInsurance ? this.form.insurance.record : '',
                validity: sameInsurance ? this.form.insurance.validity : null,
              },
            },
          },
          expenses: section.items.map(({ source: expense }) => ({
            id: expense.id,
            startDate: this.form.startDate,
            endDate,
            execution: {
              date: moment(this.form.startDate).format('YYYY-MM-DD'),
              time: moment(this.form.startDate).format('HH:mm'),
              duration: moment.duration(moment(endDate)
                .diff(moment(this.form.startDate))).asMinutes(),
            },
            accountId: '',
            expense: expense.expense,
            return: expense.return || false,
            rules: {
              requireAuthorization: false,
              requireClinicalIndication: false,
              requireWayTechnique: false,
              definition: {},
            },
            quantity: expense.quantity,
            factor: expense.factor ? expense.factor : 1,
            way: '',
            technique: '',
            value: {
              particular: expense.totalParticular / expense.quantity,
              insurance: expense.totalInsurance / expense.quantity,
              total: expense.totalParticular + expense.totalInsurance,
            },
            professional: {
              id: professional.id,
              name: professional.name,
              council: professional.council,
              specialty: { code: specialtyCode },
              degreeParticipation: 'clinical',
            },
          })),
        };

        const params = {
          expenseIds: account.expenses.map(({ expense }) => expense.id).join(','),
        };
        const { data: rules } = await this.$httpX
          .get(`/expense-insurances/${insurance.plan.id}/rules`, { params });
        (rules.items || []).forEach((item) => {
          account.expenses.forEach((expense) => {
            if (item.id === expense.expense.id && item.rules) {
              expense.rules = item.rules;
            }
          });
        });

        const tiss = settings.tiss || {};
        if (tiss.enabled) {
          this.account.type = tiss.consultationInSadt ? 'sadt' : section.guide;
        } else {
          this.account.type = plan.type || 'referral';
        }
        this.account.data = account;
        this.account.show = true;
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.working = null;
      }
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
@import "../../../billing/guide/guide.scss";
#modal-expense-register {
  $head-bg: darken($gray-color-ultra-light, 4%);
  $done-bg: #F2FAF5;
  $done-items-bg: #E3F4EA;
  $done-border: #BFE3CD;
  $done-stripe: #2FA36B;
  $done-text: #1F7A4E;
  $er-qty-width: 3rem;
  $er-value-width: 5rem;
  .er-context {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    display: flex;
    margin-bottom: $layout-spacing;
    .er-context-item {
      border-right: $border-color solid $border-width;
      flex: 1 1 0;
      padding: $layout-spacing-sm $layout-spacing;
      &:last-child {
        border-right: 0;
      }
      small {
        color: $gray-color-dark;
        display: block;
        font-size: $font-size-xs;
      }
      div {
        font-weight: 600;
      }
    }
  }
  .er-group {
    border: $border-color-dark solid $border-width;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-xl;
    overflow: hidden;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
  .er-group-head {
    align-items: center;
    background-color: $head-bg;
    border-bottom: $border-color-dark solid $border-width;
    display: flex;
    gap: $layout-spacing;
    padding: $layout-spacing-sm $layout-spacing;
    .er-group-icon {
      align-items: center;
      background-color: lighten($info-color, 40%);
      border-radius: 50%;
      color: darken($info-color, 10%);
      display: flex;
      flex: 0 0 auto;
      font-size: $font-size-sm;
      height: 1.3rem;
      justify-content: center;
      width: 1.3rem;
    }
    .er-group-title {
      flex: 1 1 auto;
      font-size: .75rem;
      font-weight: 600;
      line-height: 1.2;
      small {
        color: $gray-color-dark;
        display: block;
        font-size: $font-size-xs;
        font-weight: 400;
      }
    }
    .er-group-total {
      min-width: 7rem;
      text-align: right;
      small {
        color: $gray-color-dark;
        display: block;
        font-size: $font-size-xs;
      }
      div {
        font-variant-numeric: tabular-nums;
        font-weight: 700;
      }
    }
  }
  .er-group.particular .er-group-head .er-group-icon {
    background-color: lighten($primary-color, 42%);
    color: $primary-color;
  }
  .er-group-body {
    display: flex;
    flex-direction: column;
    gap: $layout-spacing;
    padding: $layout-spacing;
  }
  .er-section {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-left: 4px solid $warning-color;
    border-radius: $border-radius;
    padding: $layout-spacing-sm $layout-spacing $layout-spacing;
    &.er-section-pending {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      border-left-color: $warning-color;
      border-style: dashed;
      border-width: 2px;
      border-left-style: solid;
      border-left-width: 4px;
    }
    &.er-section-done {
      background-color: $done-bg;
      border-color: $done-border;
      border-left-color: $done-stripe;
    }
    &.er-section-open {
      background-color: lighten($info-color, 48%);
      border-color: lighten($info-color, 34%);
      border-left-color: $info-color;
    }
    &.er-section-closed {
      background-color: $done-bg;
      border-color: $done-border;
      border-left-color: $done-stripe;
    }
  }
  .er-section-closed {
    .er-section-head {
      background-color: rgba($done-stripe, .08);
      border-bottom-color: $done-border;
    }
    .er-items .er-item {
      border-bottom-color: $done-border;
    }
  }
  .er-section-open {
    .er-section-head {
      background-color: rgba($info-color, .07);
      border-bottom-color: lighten($info-color, 34%);
    }
    .er-items .er-item {
      border-bottom-color: lighten($info-color, 34%);
    }
  }
  .er-section-unpriced {
    color: darken($warning-color, 22%);
    font-size: $font-size-xs;
    font-weight: 600;
  }
  .er-group:not(.particular) .er-section.er-section-done {
    background-color: lighten($info-color, 48%);
    border-color: lighten($info-color, 34%);
    border-left-color: $info-color;
  }
  .er-section-head {
    align-items: center;
    background-color: rgba($dark-color, .05);
    border-bottom: $border-color solid $border-width;
    border-radius: ($border-radius - $border-width) ($border-radius - $border-width) 0 0;
    display: flex;
    gap: $layout-spacing-sm;
    margin: (-$layout-spacing-sm) (-$layout-spacing) 0;
    min-height: 1.6rem;
    padding: $layout-spacing-sm $layout-spacing;
    .er-section-title {
      flex: 0 0 6.9rem;
      font-weight: 600;
      small {
        color: $gray-color-dark;
        display: block;
        font-size: $font-size-xs;
        font-weight: 400;
      }
    }
    .er-section-state {
      align-items: center;
      background-color: $light-color;
      border: $done-stripe solid $border-width;
      border-radius: 1rem;
      color: $done-text;
      display: inline-flex;
      font-size: $font-size-xs;
      font-weight: 600;
      justify-content: center;
      line-height: 1;
      min-width: 4.4rem;
      padding: .2rem .45rem;
      svg {
        margin-right: .25rem;
      }
      &.pending {
        background-color: $light-color;
        border-color: darken(#F2C94C, 10%);
        color: darken($warning-color, 22%);
      }
    }
    .er-section-actions {
      display: flex;
      gap: .35rem;
      margin-left: auto;
      .er-delete:hover:not(:disabled) {
        background-color: $error-color;
        border-color: $error-color;
        color: $light-color;
      }
    }
  }
  .er-group:not(.particular) .er-section-done .er-section-state {
    border-color: lighten($info-color, 22%);
    color: darken($info-color, 10%);
  }
  .er-items {
    padding-top: .2rem;
  }
  .er-section-pending {
    .er-section-head {
      background-color: rgba(#F2C94C, .2);
      border-bottom-color: #F2C94C;
    }
    .er-items .er-item {
      border-bottom-color: rgba(#F2C94C, .6);
    }
  }
  .er-section-done {
    .er-section-head {
      background-color: rgba($done-stripe, .08);
      border-bottom-color: $done-border;
    }
    .er-items .er-item {
      border-bottom-color: $done-border;
    }
  }
  .er-group:not(.particular) .er-section-done {
    .er-section-head {
      background-color: rgba($info-color, .07);
      border-bottom-color: lighten($info-color, 34%);
    }
    .er-items .er-item {
      border-bottom-color: lighten($info-color, 34%);
    }
  }
  .er-item {
    align-items: center;
    border-bottom: $border-color solid $border-width;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) $er-qty-width $er-value-width;
    padding: .35rem .2rem;
    &:last-of-type {
      border-bottom: 0;
    }
    .er-item-main {
      min-width: 0;
    }
    .er-copay-note {
      margin-top: .2rem;
    }
    .er-item-service {
      display: flex;
      align-items: baseline;
      gap: .4rem;
      min-width: 0;
      small {
        color: $gray-color-dark;
        flex: 0 0 auto;
        font-size: $font-size-xs;
      }
    }
    .er-item-name {
      flex: 0 1 auto;
      font-weight: 600;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .er-item-return {
      color: $gray-color-dark;
      font-weight: 600;
    }
    .er-item-qty {
      color: $gray-color-dark;
      text-align: center;
    }
    .er-item-value {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      text-align: right;
      small {
        color: $gray-color-dark;
        display: block;
        font-size: .5rem;
        font-weight: 400;
      }
      &.text-warning {
        color: darken($warning-color, 22%);
      }
    }
  }
  .er-items-subtotal {
    align-items: baseline;
    color: $gray-color-dark;
    display: grid;
    font-size: $font-size-xs;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) $er-qty-width $er-value-width;
    padding: .3rem .2rem 0;
    text-align: right;
    strong {
      color: $dark-color;
      font-size: $font-size-sm;
      font-variant-numeric: tabular-nums;
    }
  }
  .er-group-pending {
    color: darken($warning-color, 22%);
    font-weight: 600;
  }
  .er-group-done {
    color: $done-text;
    font-size: $font-size-xs;
    font-weight: 600;
    margin-right: $layout-spacing;
    white-space: nowrap;
    svg {
      margin-right: .25rem;
    }
  }
}
</style>
